/**
 * ECS: flags product cards whose product collects line item properties through the
 * Infinite Options app, so their quick add opens the popup instead of adding straight
 * from the card (an inline add would drop the properties).
 *
 * Nothing in Liquid can tell these products apart: the app targets them by handle inside
 * the config it ships in its own bundle, which its app embed loads on every page. The PDP
 * relies on that same config (the app reads the handle from the URL and checks its rules),
 * so this module reads the config once per page — from the browser cache, since the embed
 * already fetched it — and evaluates the same rules for every card. A card that matches gets
 * data-line-item-properties="true" and data-quick-add-button="choose"; assets/quick-add.js
 * then mounts the app's fields inside the popup. Cards morphed in later (filtering, infinite
 * scroll) are picked up by a MutationObserver.
 *
 * Failure is silent by design: no app script, a blocked fetch, or an unrecognised config
 * shape leaves every card exactly as Liquid rendered it.
 */
const BUNDLE_SELECTOR = 'script[src*="customizery.js"]';
const CARD_SELECTOR = 'quick-add-component[data-product-handle]';
const CACHE_KEY = 'ecs:infinite-options-handles';

/** @typedef {{ type: string, selector?: string }} Rule */

/**
 * Pulls the option-set rules out of the app bundle's embedded config.
 * @param {string} source
 * @returns {Rule[]}
 */
function extractRules(source) {
  const marker = '"optionSets":';
  const start = source.indexOf(marker);
  if (start === -1) return [];

  // Walk to the matching bracket of the optionSets array.
  let depth = 0;
  let end = start + marker.length;
  for (; end < source.length; end++) {
    const char = source[end];
    if (char === '[' || char === '{') depth++;
    if (char === ']' || char === '}') {
      depth--;
      if (depth === 0) {
        end++;
        break;
      }
    }
  }

  const optionSets = JSON.parse(`{${source.slice(start, end)}}`).optionSets;
  if (!Array.isArray(optionSets)) return [];

  return optionSets.flatMap((set) => (Array.isArray(set.ruleSets) ? set.ruleSets.flat() : []));
}

/** @param {string} url */
function handleFromUrl(url) {
  const parts = url.replace(/\/$/, '').split('/');
  return parts.length >= 2 && parts[parts.length - 2] === 'products' ? parts[parts.length - 1].split('?')[0] : '';
}

/**
 * Same matching the app applies on the PDP. Anything it cannot evaluate counts as a match:
 * a needless popup still adds correctly, a needless inline add loses the properties.
 * @param {Rule} rule
 * @param {{ handle: string, tags: string[] }} product
 */
function ruleMatches(rule, product) {
  const selector = (rule.selector ?? '').toLowerCase();
  switch (rule.type) {
    case 'product_handle':
      return product.handle === selector;
    case 'product_url':
      return product.handle === handleFromUrl(selector);
    case 'tag':
      return product.tags.includes(selector);
    default:
      return true;
  }
}

/** @param {Rule[]} rules @param {Element} card */
function flagCard(rules, card) {
  if (!(card instanceof HTMLElement) || card.dataset.lineItemProperties === 'true') return;
  if (card.dataset.requiresSellingPlan === 'true') return;

  const product = {
    handle: (card.dataset.productHandle ?? '').toLowerCase(),
    tags: (card.dataset.productTags ?? '').split(',').filter(Boolean),
  };
  if (!product.handle || !rules.some((rule) => ruleMatches(rule, product))) return;

  card.dataset.lineItemProperties = 'true';
  card.setAttribute('data-quick-add-button', 'choose');
}

/** @returns {Promise<Rule[]>} */
async function loadRules() {
  const bundle = document.querySelector(BUNDLE_SELECTOR);
  if (!(bundle instanceof HTMLScriptElement) || !bundle.src) return [];

  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { src, rules } = JSON.parse(cached);
      if (src === bundle.src && Array.isArray(rules)) return rules;
    }
  } catch {
    // Storage unavailable — fall through to the fetch.
  }

  const response = await fetch(bundle.src);
  if (!response.ok) return [];
  const rules = extractRules(await response.text());

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ src: bundle.src, rules }));
  } catch {
    // Storage unavailable — the fetch is served from cache anyway.
  }

  return rules;
}

loadRules()
  .then((rules) => {
    if (!rules.length) return;

    const flagAll = () => document.querySelectorAll(CARD_SELECTOR).forEach((card) => flagCard(rules, card));
    flagAll();
    new MutationObserver(flagAll).observe(document.body, { childList: true, subtree: true });
  })
  .catch((error) => {
    console.warn('[quick-add-line-item-properties] Could not read the Infinite Options config:', error);
  });
